import { useEffect, useState, useCallback } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { EnrichedProperty, PropertyEditForm, PropertyMode, PropertyTab } from './types';

const EMPTY_FORM: PropertyEditForm = {
  property_name: '',
  property_code: '',
  property_category: 'hospitality',
  property_type: 'hotel',
  hospitality_tier: 'assured',
  contract_status: 'onboarding',
  total_beds: 0,
  total_rooms: 0,
  ambassador_id: '',
  assigned_manager_id: '',
  customer_id: '',
  city: '',
  address_line1: '',
  slug: '',
  country: '',
  province: '',
  is_published: false,
  is_featured: false,
  tagline: '',
  description: '',
  hero_image_url: '',
  brand_logo_url: '',
  gallery_images: [],
  amenity_tags: [],
  public_email: '',
  public_phone: '',
  whatsapp_number: '',
  listing_links: [],
  certified_since: '',
  external_rating: '',
  external_rating_source: '',
  external_review_count: '',
  charter_delivery_notes: '',
  charter_delivery_to: '',
  charter_delivery_address: '',
  charter_delivered: false,
  assured_until: '',
};

function propertyToForm(p: EnrichedProperty): PropertyEditForm {
  return {
    property_name: p.property_name || '',
    property_code: p.property_code || '',
    property_category: p.property_category || 'hospitality',
    property_type: p.property_type || 'hotel',
    hospitality_tier: p.hospitality_tier || 'assured',
    contract_status: p.contract_status || 'onboarding',
    total_beds: p.total_beds || 0,
    total_rooms: p.total_rooms || 0,
    ambassador_id: p.ambassador_id || '',
    assigned_manager_id: p.assigned_manager_id || '',
    customer_id: p.customer_id || '',
    city: p.city || '',
    address_line1: p.address_line1 || '',
    slug: p.slug || '',
    country: p.country || '',
    province: p.province || '',
    is_published: !!p.is_published,
    is_featured: !!p.is_featured,
    tagline: p.tagline || '',
    description: p.description || '',
    hero_image_url: p.hero_image_url || '',
    brand_logo_url: p.brand_logo_url || '',
    gallery_images: p.gallery_images || [],
    amenity_tags: p.amenity_tags || [],
    public_email: p.public_email || '',
    public_phone: p.public_phone || '',
    whatsapp_number: p.whatsapp_number || '',
    listing_links: p.listing_links || [],
    certified_since: p.certified_since || '',
    external_rating: p.external_rating != null ? String(p.external_rating) : '',
    external_rating_source: p.external_rating_source || '',
    external_review_count: p.external_review_count != null ? String(p.external_review_count) : '',
    charter_delivery_notes: p.charter_delivery_notes || '',
    charter_delivery_to: p.charter_delivery_to || '',
    charter_delivery_address: p.charter_delivery_address || '',
    charter_delivered: !!p.charter_delivered_at,
    assured_until: p.assured_until || '',
  };
}

export function usePropertyDetail(
  supabase: SupabaseClient,
  propertyId: string | null,
  mode: PropertyMode,
  onRefresh?: () => Promise<void> | void,
  initialCustomerId?: string,
  initialTab?: PropertyTab,
) {
  const [property, setProperty] = useState<EnrichedProperty | null>(null);
  const [form, setForm] = useState<PropertyEditForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(propertyId !== null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Default landing tab: caller-supplied initialTab if any (e.g. the
  // Portfolio board task cards deep-link to Rooms). Otherwise:
  // existing properties land on Overview (everything about the
  // property at a glance). New properties land on Property (the edit
  // form) since Overview has nothing to show until a property exists.
  const [activeTab, setActiveTab] = useState<PropertyTab>(
    initialTab ?? (propertyId ? 'overview' : 'property'),
  );

  // When the modal is opened from the "Create POS Property" CTA on an
  // orphan customer, pre-seed the form with that customer and load the
  // display name + email so the Linked Accounts row shows the name
  // instead of just the UUID. Kept separate from `property` because the
  // property doesn't exist yet in create mode.
  const [seededCustomer, setSeededCustomer] = useState<EnrichedProperty['customer'] | null>(null);

  useEffect(() => {
    if (mode !== 'create' || !initialCustomerId) {
      setSeededCustomer(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('customers')
        .select('id, customer_name, company_name, email, phone, email_bouncing, email_last_bounce_at')
        .eq('id', initialCustomerId)
        .maybeSingle();
      if (cancelled) return;
      setForm((f) => ({ ...f, customer_id: initialCustomerId }));
      if (data) setSeededCustomer(data as EnrichedProperty['customer']);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialCustomerId]);

  const loadProperty = useCallback(async () => {
    if (!propertyId) {
      setProperty(null);
      setForm(EMPTY_FORM);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('hosp_properties')
      .select(`
        *,
        customer:customers(id, customer_name, company_name, email, phone, email_bouncing, email_last_bounce_at),
        ambassador:users!hosp_properties_ambassador_id_fkey(id, first_name, last_name, email),
        manager:users!hosp_properties_assigned_manager_id_fkey(id, first_name, last_name, email)
      `)
      .eq('id', propertyId)
      .single();

    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }

    const enriched = data as unknown as EnrichedProperty;
    setProperty(enriched);
    setForm(propertyToForm(enriched));
    setLoading(false);
  }, [supabase, propertyId]);

  useEffect(() => {
    loadProperty();
  }, [loadProperty]);

  const handleSave = useCallback(async (overrides?: Partial<PropertyEditForm>): Promise<boolean> => {
    if (mode !== 'edit' || !property) return false;
    const effective: PropertyEditForm = { ...form, ...(overrides || {}) };
    if (!effective.property_name.trim()) {
      setError('Property name is required');
      return false;
    }
    setSaving(true);
    setError(null);
    const payload: Record<string, unknown> = {
      property_name: effective.property_name,
      property_code: effective.property_code || null,
      property_category: effective.property_category || 'hospitality',
      property_type: effective.property_type || 'hotel',
      hospitality_tier: effective.hospitality_tier || 'assured',
      contract_status: effective.contract_status || 'onboarding',
      total_beds: effective.total_beds || 0,
      total_rooms: effective.total_rooms || 0,
      ambassador_id: effective.ambassador_id || null,
      assigned_manager_id: effective.assigned_manager_id || null,
      customer_id: effective.customer_id || null,
      city: effective.city || null,
      address_line1: effective.address_line1 || null,
      slug: effective.slug || null,
      country: effective.country || null,
      province: effective.province || null,
      is_published: effective.is_published,
      is_featured: effective.is_featured,
      tagline: effective.tagline || null,
      description: effective.description || null,
      hero_image_url: effective.hero_image_url || null,
      brand_logo_url: effective.brand_logo_url || null,
      gallery_images: effective.gallery_images,
      amenity_tags: effective.amenity_tags,
      public_email: effective.public_email || null,
      public_phone: effective.public_phone || null,
      whatsapp_number: effective.whatsapp_number || null,
      listing_links: effective.listing_links,
      certified_since: effective.certified_since || null,
      external_rating: effective.external_rating ? parseFloat(effective.external_rating) : null,
      external_rating_source: effective.external_rating_source || null,
      external_review_count: effective.external_review_count ? parseInt(effective.external_review_count, 10) : null,
      charter_delivery_notes: effective.charter_delivery_notes || null,
      charter_delivery_to: effective.charter_delivery_to || null,
      charter_delivery_address: effective.charter_delivery_address || null,
      charter_delivered_at: effective.charter_delivered
        ? (property.charter_delivered_at || new Date().toISOString())
        : null,
      assured_until: effective.assured_until || null,
    };
    const { error: updErr } = await supabase.from('hosp_properties').update(payload).eq('id', property.id);
    setSaving(false);
    if (updErr) {
      setError(updErr.message);
      return false;
    }
    if (overrides) setForm((f) => ({ ...f, ...overrides }));
    await loadProperty();
    await onRefresh?.();
    return true;
  }, [supabase, property, form, mode, loadProperty, onRefresh]);

  const handleCreate = useCallback(async (): Promise<{ id: string } | null> => {
    if (mode !== 'create') return null;

    // Customer Gate: every POS property must be created against a confirmed,
    // contactable customer with a deliverable physical address. These checks
    // catch the failure modes that break every downstream surface (Charter
    // delivery has nowhere to post, branded comms have nowhere to send,
    // duplicate customer records fork the invoice trail). See
    // docs/guides/SOP-POS-ONBOARDING.md Section 2.
    if (!form.property_name.trim()) {
      setError('Property name is required.');
      return null;
    }
    if (!form.customer_id) {
      setError('Link a customer first. Every property must belong to a confirmed billing customer.');
      return null;
    }
    const missingAddress: string[] = [];
    if (!form.address_line1?.trim()) missingAddress.push('street address');
    if (!form.city?.trim()) missingAddress.push('city');
    if (!form.province?.trim()) missingAddress.push('province / region');
    if (missingAddress.length > 0) {
      setError(`Property ${missingAddress.join(', ')} ${missingAddress.length === 1 ? 'is' : 'are'} required. Charters cannot be posted and Verify cannot display a property without this.`);
      return null;
    }

    // Verify the linked customer is contactable (has an email on file).
    // Read fresh from the DB rather than trusting the picker payload, in
    // case the email has been cleared since the picker opened.
    const { data: cust, error: custErr } = await supabase
      .from('customers')
      .select('email, customer_name')
      .eq('id', form.customer_id)
      .maybeSingle();
    if (custErr || !cust) {
      setError('Could not load the linked customer. Try linking again.');
      return null;
    }
    if (!cust.email || !String(cust.email).trim()) {
      setError(`Linked customer "${cust.customer_name}" has no email on file. Add one before creating the property, since the welcome, invoice, and Suite invite all need it.`);
      return null;
    }

    setSaving(true);
    setError(null);
    const payload: Record<string, unknown> = {
      property_name: form.property_name,
      property_code: form.property_code || null,
      property_category: form.property_category || 'hospitality',
      property_type: form.property_type || 'hotel',
      hospitality_tier: form.hospitality_tier || 'assured',
      contract_status: form.contract_status || 'onboarding',
      total_beds: form.total_beds || 0,
      total_rooms: form.total_rooms || 0,
      ambassador_id: form.ambassador_id || null,
      assigned_manager_id: form.assigned_manager_id || null,
      customer_id: form.customer_id || null,
      city: form.city || null,
      address_line1: form.address_line1 || null,
      slug: form.slug || null,
      country: form.country || null,
      province: form.province || null,
    };

    if (form.customer_id) {
      await supabase.from('customers').update({ is_pos: true }).eq('id', form.customer_id);
    }

    const { data, error: insErr } = await supabase
      .from('hosp_properties')
      .insert(payload)
      .select('id')
      .single();
    setSaving(false);
    if (insErr) {
      setError(insErr.message);
      return null;
    }
    await onRefresh?.();
    return data as { id: string };
  }, [supabase, form, mode, onRefresh]);

  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (mode !== 'edit' || !property) return false;
    setDeleting(true);
    setError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pos-property-delete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.session?.access_token}`,
          },
          body: JSON.stringify({ property_id: property.id }),
        },
      );
      const result = await res.json();
      setDeleting(false);
      if (!res.ok || !result.message) {
        setError(result.error || 'Delete failed');
        return false;
      }
      await onRefresh?.();
      return true;
    } catch (err: unknown) {
      setDeleting(false);
      setError(err instanceof Error ? err.message : 'Delete failed');
      return false;
    }
  }, [supabase, property, mode, onRefresh]);

  return {
    property,
    seededCustomer,
    form,
    setForm,
    loading,
    saving,
    deleting,
    error,
    activeTab,
    setActiveTab,
    loadProperty,
    handleSave,
    handleCreate,
    handleDelete,
  };
}
